import {
  DefaultError,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { IInfinitePageStatisticsResponse } from "services/types";
import { apiClient } from "utils";
import { useInfiniteHandlerWithStatistics } from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  IGetSettlementLabelInfiniteParams,
  IResponseSettlementPeriodsLightList,
  ISettlementLabelPageResponse,
  ISettlementLabelResponse,
  ISettlementLabelStatistics,
} from "./types";

export const useGetSettlementSettlementsInfinite = (
  params?: IGetSettlementLabelInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  const {
    getNextPageParam,
    infiniteHandlerWithStatistics,
    defaultPageParams,
    statistics,
  } = useInfiniteHandlerWithStatistics<
    ISettlementLabelResponse,
    ISettlementLabelStatistics
  >(params);

  const query = useInfiniteQuery<ISettlementLabelResponse[], Error>({
    queryKey: ["settlement-settlements", params],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiClient({
        method: "GET",
        url: "Settlement/Settlements",
        params: {
          ...params,
          ...defaultPageParams,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<
          ISettlementLabelResponse,
          ISettlementLabelStatistics
        >
      >(response, {
        groupName: `useGetSettlementSettlementsInfinite - ${pageParam}`,
      });

      return infiniteHandlerWithStatistics(value);
    },
    initialPageParam: defaultPageParams.page ?? 0,
    getNextPageParam,
  });

  return {
    ...query,
    statistics,
  };
};

export const useGetSettlementStatistics = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISettlementLabelStatistics, DefaultError>({
    queryKey: ["settlement-statistics"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Settlement/Settlements`,
        params: {
          page: 0,
          itemCount: 1,
        },
      });

      const { statistics } =
        await responseHandler<ISettlementLabelPageResponse>(response, {
          groupName: `useGetSettlementStatistics`,
        });

      return statistics;
    },
  });
};

export const useGetSettlementFile = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ["settlement-file", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Settlement/File/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetSettlementPeriodsLightList = () => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IResponseSettlementPeriodsLightList[], DefaultError>({
    queryKey: ["settlementPeriodsLightList"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "SettlementPeriods/LightList",
      });

      return responseHandler(response, {
        groupName: "useGetSettlementPeriodsLightList",
      });
    },
  });
};
